/**
 * The copy and the markup of the consent surfaces. Wording changes here,
 * and the component keeps the state and the events.
 */

import { ConsentCategory } from './consent-categories';

export const COPY = {
  banner: {
    ariaLabel: 'Cookie consent',
    title: 'We use cookies',
    message:
      'We use cookies to operate the site, measure traffic, and deliver relevant ads. Analytics and advertising cookies are only set upon approval.'
  },
  notice: {
    ariaLabel: 'Privacy signal',
    title: 'We honored your privacy signal',
    message:
      'Your browser sent a Global Privacy Control signal, so we turned off analytics and advertising tracking for you.'
  },
  panel: {
    ariaLabel: 'Cookie preferences',
    title: 'Cookie preferences',
    intro: 'Choose what loads on this site.',
    closeLabel: 'Close'
  },
  link: 'Privacy policy',
  actions: {
    accept: 'Accept all',
    reject: 'Reject all',
    manage: 'Manage preferences',
    dismiss: 'Dismiss',
    save: 'Save preferences'
  }
};

const linkHtml = (privacyUrl: string): string =>
  `<a class="kl-consent__link" href="${privacyUrl}">${COPY.link}</a>`;

const buttonHtml = (choice: string, label: string, primary = false): string =>
  `<button type="button" class="kl-consent__btn${
    primary ? ' kl-consent__btn--accept' : ''
  }" data-consent="${choice}">${label}</button>`;

export const bannerHtml = (privacyUrl: string, message: string): string => `
  <div class="kl-consent__body">
    <p class="kl-consent__title">${COPY.banner.title}</p>
    <p class="kl-consent__text">
      ${message}
      ${linkHtml(privacyUrl)}
    </p>
  </div>
  <div class="kl-consent__actions">
    ${buttonHtml('accept', COPY.actions.accept, true)}
    ${buttonHtml('reject', COPY.actions.reject)}
    ${buttonHtml('manage', COPY.actions.manage)}
  </div>
`;

export const noticeHtml = (privacyUrl: string): string => `
  <div class="kl-consent__body">
    <p class="kl-consent__title">${COPY.notice.title}</p>
    <p class="kl-consent__text">
      ${COPY.notice.message}
      ${linkHtml(privacyUrl)}
    </p>
  </div>
  <div class="kl-consent__actions">
    ${buttonHtml('manage', COPY.actions.manage)}
    ${buttonHtml('dismiss', COPY.actions.dismiss)}
  </div>
`;

export const rowHtml = (category: ConsentCategory, on: boolean): string => {
  const description = category.description
    ? `<span class="kl-consent-panel__desc">${category.description}</span>`
    : '';

  return `
    <li class="kl-consent-panel__row">
      <span class="kl-consent-panel__label">
        ${category.label}
        ${description}
      </span>
      <label class="kl-switch">
        <input type="checkbox" data-category="${category.id}"
          ${on ? 'checked' : ''} ${category.locked ? 'disabled' : ''}
          aria-label="${category.label}">
        <span class="kl-switch__track"></span>
      </label>
    </li>
  `;
};

export const panelHtml = (privacyUrl: string, rows: string): string => `
  <div class="kl-consent-panel__backdrop" data-panel="close"></div>
  <div class="kl-consent-panel__dialog">
    <div class="kl-consent-panel__head">
      <div>
        <p class="kl-consent-panel__title">${COPY.panel.title}</p>
        <p class="kl-consent-panel__intro">
          ${COPY.panel.intro}
          ${linkHtml(privacyUrl)}
        </p>
      </div>
      <button type="button" class="kl-consent-panel__close" data-panel="close" aria-label="${COPY.panel.closeLabel}">&times;</button>
    </div>
    <ul class="kl-consent-panel__rows">
      ${rows}
    </ul>
    <div class="kl-consent-panel__foot">
      <div class="kl-consent-panel__bulk">
        ${buttonHtml('accept', COPY.actions.accept)}
        ${buttonHtml('reject', COPY.actions.reject)}
      </div>
      ${buttonHtml('save', COPY.actions.save, true)}
    </div>
  </div>
`;
